import {v3} from "../short3.js";

//statische Hilfsvariablen, die in isIntersectingTriangle benutzt werden,
//aber aus Performancegründen nicht bei jedem Methodenaufruf neu erzeugt werden

const vA = v3();
const vB = v3();
const vC = v3();

const tempA = v3();
const tempB = v3();
const tempC = v3();

/**
 * Abstrakte Basisklasse für alle IntersectionHandler
 * @see http://preview.objectcode.de/HoloExamples/#oc-threejs.TestIntersectionHandler
 */
export default class AbstractIntersectionHandler {

    /**
     * @param options
     * @example
     * options = {
     *   // sollen auch Unterobjecte getestet werden?
     *   recursive: true,
     * }
     */
    constructor(objects,options) {
        // größtenteils von Raycaster und Mesh geklaut

        this.objects = objects;
        this.recursive = (options && options.recursive) || true;

        this.boundingSphere = new THREE.Sphere();
        this.inverseMatrix = new THREE.Matrix4();

        this.intersectionsOfCurrentObject = [];
    }

    /**
     * Führt einen Schnittstest gegen mehrere Objecte durch
     */
    intersectObjects(objects) {
        objects.forEach(object => this.intersectObject(object));
    }

    /**
     * Führt einen Schnittstest gegen ein einzelnes Object durch
     */
    intersectObject(object) {
        if (object.visible === false) {
            return;
        }
        if (object instanceof THREE.Mesh) {

            const geometry = object.geometry;
            const material = object.material;

            if (!material) {
                return;
            }

            if (geometry.boundingSphere === null) {
                geometry.computeBoundingSphere();
            }

            const matrixWorld = object.matrixWorld;

            this.boundingSphere.copy(geometry.boundingSphere);
            this.boundingSphere.applyMatrix4(matrixWorld);

            if (!this.isIntersectionSphere(this.boundingSphere)) {
                return;
            }

            this.inverseMatrix.copy(matrixWorld).invert();

            this.getGeometryInLocalSpace()
                .copy(this.getGeometryInWorldSpace())
                .applyMatrix4(this.inverseMatrix);


            this.intersectionsOfCurrentObject.length=0;

            if (geometry instanceof THREE.BufferGeometry) {
                //console.log('BufferGeometry');
                this.intersectBufferGeometry(object);
            } else if (geometry instanceof THREE.Geometry) {
                //console.log('Geometry');
                this.intersectGeometry(object);
            }

            this.flushIntersectionsOfCurrentObject(this.intersectionsOfCurrentObject);
        }
        if (this.recursive) {
            this.intersectObjects(object.children);
        }
    }

    /**
     * interne Methode
     */
    intersectBufferGeometry(object) {
        let a, b, c;
        const geometry = object.geometry;
        const index = geometry.index;
        const attributes = geometry.attributes;
        const positions = attributes.position.array;

        if (index !== null) {
            const indices = index.array;
            for (var i = 0, l = indices.length; i < l; i += 3) {
                a = indices[i];
                b = indices[i + 1];
                c = indices[i + 2];
                this.checkBufferGeometryIntersection(object, positions, a, b, c, (intersection) => {
                    // triangle number in indices buffer semantics
                    intersection.faceIndex = Math.floor(i / 3);
                });
            }
        } else {
            for (let i = 0, l = positions.length; i < l; i += 9) {
                a = i / 3;
                b = a + 1;
                c = a + 2;
                this.checkBufferGeometryIntersection(object, positions, a, b, c, (intersection) => {
                    // triangle number in positions buffer semantics
                    intersection.index = a;
                });
            }
        }
    }

    /**
     * interne Methode
     */
    checkBufferGeometryIntersection(object, positions, a, b, c, enhanceIntersectionCallback) {

        vA.fromArray(positions, a * 3);
        vB.fromArray(positions, b * 3);
        vC.fromArray(positions, c * 3);

        this.checkIntersection(object, vA, vB, vC, (intersection) => {
            intersection.faceIndex = a;
            intersection.face = new THREE.Face3(a, b, c, THREE.Triangle.getNormal(vA, vB, vC, v3()));
            enhanceIntersectionCallback(intersection);
        });
    }


    /**
     * interne Methode
     */
    intersectGeometry(object) {
        let fvA, fvB, fvC;
        const geometry = object.geometry;
        const material = object.material;
        const isFaceMaterial = material instanceof THREE.MeshFaceMaterial;
        const materials = isFaceMaterial === true ? material.materials : null;

        const vertices = geometry.vertices;
        const faces = geometry.faces;
        const faceVertexUvs = geometry.faceVertexUvs[0];

        for (let f = 0, fl = faces.length; f < fl; f++) {

            const face = faces[f];
            const faceMaterial = isFaceMaterial === true ? materials[face.materialIndex] : material;

            if (faceMaterial === undefined) {
                continue;
            }

            fvA = vertices[face.a];
            fvB = vertices[face.b];
            fvC = vertices[face.c];

            if (faceMaterial.morphTargets === true) {

                const morphTargets = geometry.morphTargets;
                const morphInfluences = this.morphTargetInfluences;

                vA.set(0, 0, 0);
                vB.set(0, 0, 0);
                vC.set(0, 0, 0);

                for (let t = 0, tl = morphTargets.length; t < tl; t++) {

                    const influence = morphInfluences[t];

                    if (influence === 0) {
                        continue;
                    }

                    const targets = morphTargets[t].vertices;

                    vA.addScaledVector(tempA.subVectors(targets[face.a], fvA), influence);
                    vB.addScaledVector(tempB.subVectors(targets[face.b], fvB), influence);
                    vC.addScaledVector(tempC.subVectors(targets[face.c], fvC), influence);

                }

                vA.add(fvA);
                vB.add(fvB);
                vC.add(fvC);

                fvA = vA;
                fvB = vB;
                fvC = vC;

            }

            this.checkIntersection(object, fvA, fvB, fvC, (intersection) => {
                intersection.face = face;
                intersection.faceIndex = f;
            });
        }
    }

    /**
     * interne Methode
     */
    flushIntersectionsOfCurrentObject(intersectionsOfCurrentObject) {
        intersectionsOfCurrentObject.forEach(this.intersectioncallback);
    }

    /**
     * interne Methode
     */
    intersectioncallback (i) {
        console.log(i);
    }

    /**
     * interne Methode
     */
    checkIntersection(object, pA, pB, pC, enhanceIntersectionCallback) {

        let a, b, c, backfaceCulling;
        const material = object.material;

        if (material.side === THREE.BackSide) {
            a = pC;
            b = pB;
            c = pA;
            backfaceCulling = true;
        } else {
            a = pA;
            b = pB;
            c = pC;
            backfaceCulling = material.side !== THREE.DoubleSide;
        }

        this.intersectTriangle(a, b, c, backfaceCulling, object.matrixWorld, (intersection) => {
            enhanceIntersectionCallback(intersection);
            intersection.object = object;
            this.intersectionsOfCurrentObject.push(intersection)
        });
    }

    // abstract methods ----

    /**
     * abstrakte Methode
     */
    isIntersectionSphere (sphere) {
    }


    /**
     * abstrakte Methode
     */
    intersectTriangle (a, b, c, backfaceCulling, worldMatrix, pushIntersectionCallback) {
    }

    /**
     * abstrakte Methode
     */
    getGeometryInLocalSpace() {
        return null;
    }

    /**
     * abstrakte Methode
     */
    getGeometryInWorldSpace () {
        return null;
    }

    /**
     * abstrakte Methode
     */
    debugShow () {
        return null;
    }

    // ---------------------

    /**
     * interne Methode
     */
    applyMatrixOnPoints (matrix, points) {
        points.forEach(p=>p.applyMatrix4(matrix));
        return points;
    }

    /**
     * interne Methode
     */
    intersect (intersectioncallback) {
        this.intersectioncallback = intersectioncallback;
        if (this.objects instanceof THREE.Object3D) {
            this.objects.updateMatrixWorld();
            this.intersectObject(this.objects);
        } else {
            this.objects.forEach(o=>o.updateMatrixWorld());
            this.intersectObjects(this.objects);
        }
    }

}
